import { useCallback, useEffect, useState } from "react";
import { Camera, Film, Radio } from "lucide-react";
import { toast } from "sonner";
import { ccApi, errMsg, fmtTs } from "./ccApi";
import { ActionBtn, Empty, Pill, Section } from "./ccUi";
import ReasonDialog from "./ReasonDialog";
import DiagnosticViewer from "./DiagnosticViewer";
import { captureAndUpload } from "./evidence";

export default function LiveTab({ target, setTarget, me }) {
  const [list, setList] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [grant, setGrant] = useState(null);

  const refresh = useCallback(() => ccApi.liveActive().then((d) => setList(d.streams)).catch(() => {}), []);
  const load = useCallback((id) => ccApi.live(id).then(setCtx).catch((e) => toast.error(errMsg(e, "Live not found"))), []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (target?.type === "live" && target.id) load(target.id); else setCtx(null); }, [target, load]);

  const closeGrant = useCallback(() => {
    if (grant) ccApi.revokeGrant(grant.grant.id).catch(() => {});
    setGrant(null);
  }, [grant]);
  useEffect(() => () => { if (grant) ccApi.revokeGrant(grant.grant.id).catch(() => {}); }, [grant]);

  const run = (spec, fn) => setDlg({ ...spec, fn });
  const submit = async ({ reason, confirm }) => {
    try {
      const out = await dlg.fn({ reason, confirm });
      if (out?.status === "clip_unavailable") toast.warning(out.message, { duration: 8000 });
      else if (out?.grant) { setGrant(out); toast.success("Diagnostic access granted · audited"); }
      else toast.success(`${dlg.title} — logged`);
      setDlg(null);
      if (ctx) load(ctx.stream.id);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const s = ctx?.stream;
  const sid = s?.id;
  const kind = s ? (s.is_community ? "Community" : s.is_locked ? "Locked / private" : "Public") : "";

  return (
    <div className="space-y-3">
      {!s && (
        <Section title={`Live now · ${list.length}`} testid="cc-live-list" right={<ActionBtn testid="cc-live-refresh" onClick={refresh}>Refresh</ActionBtn>}>
          {list.length === 0 && <Empty>No active lives.</Empty>}
          {list.map((l) => (
            <button key={l.id} type="button" data-testid={`cc-live-row-${l.id}`} onClick={() => setTarget({ type: "live", id: l.id })}
              className="w-full flex items-center gap-2 text-left text-xs px-2 py-2 rounded hover:bg-white/5">
              <Radio size={12} className="text-rose-400" />
              <span className="text-white">{l.host?.display_name}</span>
              <span className="text-zinc-400 truncate flex-1">{l.title}</span>
              {l.is_community && <Pill tone="blue">community</Pill>}{l.is_locked && <Pill tone="amber">locked</Pill>}{l.chat_frozen && <Pill tone="red">chat frozen</Pill>}
              <span className="text-zinc-500">{l.viewer_count ?? 0} 👁</span>
            </button>
          ))}
        </Section>
      )}
      {s && (
        <>
          <div data-testid="cc-live-card" className="space-y-1">
            <button type="button" data-testid="cc-live-back" onClick={() => setTarget(null)} className="text-[11px] text-zinc-500 hover:text-white">← all lives</button>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-white font-semibold">{s.title || "Untitled live"}</span>
              <Pill tone={s.is_live ? "red" : "zinc"} testid="cc-live-status">{s.is_live ? "LIVE" : "ended"}</Pill>
              <Pill tone={s.is_community ? "blue" : s.is_locked ? "amber" : "zinc"}>{kind}</Pill>
              {s.chat_frozen && <Pill tone="red" testid="cc-live-chat-frozen">chat frozen</Pill>}
            </div>
            <div className="text-xs text-zinc-400">Host <button type="button" className="text-white underline-offset-2 hover:underline" data-testid="cc-live-open-host" onClick={() => setTarget({ type: "user", id: ctx.host?.id, handle: ctx.host?.handle, _tab: "member" })}>{ctx.host?.display_name}</button> · started {fmtTs(s.started_at)} · {s.viewer_count ?? 0} viewers · {ctx.reports.length} report(s)</div>
            <div className="text-[11px] text-zinc-500 font-mono">live id {s.id}</div>
          </div>
          {s.is_live && (
            <Section title="Live controls" testid="cc-live-controls">
              <div className="flex flex-wrap gap-2">
                <ActionBtn tone={s.chat_frozen ? "good" : "warn"} testid="cc-live-freeze-chat" onClick={() => run({ title: s.chat_frozen ? "Unfreeze chat" : "Freeze chat", cta: s.chat_frozen ? "Unfreeze" : "Freeze" }, ({ reason }) => ccApi.liveAction(sid, "freeze-chat", { reason, frozen: !s.chat_frozen }))}>{s.chat_frozen ? "Unfreeze chat" : "Freeze chat"}</ActionBtn>
                <ActionBtn tone="danger" testid="cc-live-force-end" onClick={() => run({ title: "Force-end this Live", description: "Ends the broadcast for everyone immediately.", highImpact: true, cta: "End Live" }, ({ reason, confirm }) => ccApi.liveAction(sid, "force-end", { reason, confirm }))}>Force-end</ActionBtn>
                {me?.can?.diagnostics && !grant && (
                  <ActionBtn tone="default" testid="cc-live-diagnostics" onClick={() => run({ title: "Open read-only diagnostics", description: "Hidden, subscribe-only view of this live (locked / private / Vault / Community). Access is audited and expires in 15 minutes.", cta: "Open diagnostics" }, ({ reason }) => ccApi.diagnosticAccess(sid, reason))}>Diagnostics view</ActionBtn>
                )}
                {me?.can?.evidence && (
                  <>
                    <ActionBtn testid="cc-live-evidence-shot" onClick={() => run({ title: "Capture evidence (screenshot)", cta: "Capture" }, ({ reason }) => captureAndUpload({ target: { type: "live", id: sid, accountId: s.host_id }, reasonCode: reason.slice(0, 60) }))}><Camera size={12} className="inline mr-1" />Screenshot</ActionBtn>
                    <ActionBtn testid="cc-live-evidence-clip" onClick={() => run({ title: "Capture short Live clip (30s)", description: me?.live_clip_available ? "Records via LiveKit Egress." : "No egress evidence destination is enabled here — the request will be recorded as clip_unavailable.", cta: "Request clip" }, ({ reason }) => ccApi.liveClip({ stream_id: sid, account_id: s.host_id, reason_code: reason.slice(0, 60), seconds: 30 }))}><Film size={12} className="inline mr-1" />Live clip</ActionBtn>
                  </>
                )}
              </div>
            </Section>
          )}
          {grant && <DiagnosticViewer grant={grant} onClose={closeGrant} />}
          <Section title={`Participants · ${ctx.participants.length}`} testid="cc-live-participants">
            {ctx.participants.length === 0 && <Empty>No presence rows right now.</Empty>}
            {ctx.participants.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-xs" data-testid={`cc-live-participant-${p.handle}`}>
                <button type="button" className="text-zinc-200 hover:underline" onClick={() => setTarget({ type: "user", id: p.id, handle: p.handle, _tab: "member" })}>{p.display_name}</button>
                {s.is_live && p.id !== s.host_id && <ActionBtn tone="danger" testid={`cc-live-remove-${p.handle}`} onClick={() => run({ title: `Remove ${p.display_name} from Live`, cta: "Remove" }, ({ reason }) => ccApi.liveAction(sid, "remove", { reason, user_id: p.id }))}>Remove</ActionBtn>}
              </div>
            ))}
          </Section>
          {ctx.access_grants.length > 0 && (
            <Section title="Diagnostic access history" testid="cc-live-grants">
              {ctx.access_grants.map((g) => <div key={g.id} className="text-xs text-zinc-400">@{g.actor_handle} ({g.actor_role}) · {fmtTs(g.created_at)} · “{g.reason}” {g.revoked_at ? <Pill>revoked</Pill> : <Pill tone="blue">active/expired by TTL</Pill>}</div>)}
            </Section>
          )}
        </>
      )}
      <ReasonDialog spec={dlg} onClose={() => setDlg(null)} onSubmit={submit} />
    </div>
  );
}
